/**
 * « Déjà client » — bandeau de fiche, bouton et modale de marquage.
 *
 * Ce que ça dit, et qu'aucun statut ne disait : cette entreprise ACHÈTE, et
 * quoi. À l'ouverture de la fiche, la première ligne lue est « Client — Caisse
 * connectée depuis le … » ; côté ciblage, elle ne remonte plus dans les listes
 * de prospection, le pipeline, les relances automatiques ni les campagnes
 * d'emailing (la règle est tenue par le serveur, voir crm/clients.py).
 *
 * À ne pas confondre avec « Ne plus contacter » (ExclusionControls) : ici rien
 * n'est interdit. On appelle son client, on le relance, on le dépanne — les
 * boutons de la fiche restent actifs. Ce qu'on retire, c'est la prospection.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, X, Handshake } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";

/** Noms des solutions achetées, quelle que soit la richesse de la réponse. */
export function clientSolutionNames(company) {
  return (company?.client_solutions || []).map((s) => s.nom).filter(Boolean);
}

export function ClientBanner({ company }) {
  if (!company?.client) return null;
  const noms = clientSolutionNames(company);
  return (
    <div data-testid="client-banner"
      className="mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <BadgeCheck size={22} className="mt-0.5 shrink-0 text-emerald-600" />
      <div className="min-w-0">
        <div className="font-heading font-bold tracking-tight text-emerald-800">
          ✅ CLIENT{noms.length > 0 && <> — {noms.join(", ")}</>}
        </div>
        <div className="mt-1 text-sm text-emerald-700">
          {company.client_since && <>Depuis le {new Date(company.client_since).toLocaleDateString("fr-FR")} · </>}
          Hors prospection : cette fiche n'entre plus dans les listes d'appel, le pipeline, les relances ni les campagnes.
          {company.client_marked_by && <> · Marquée par {company.client_marked_by}</>}
        </div>
        {company.client_note && <div className="mt-1 text-sm text-emerald-800">{company.client_note}</div>}
      </div>
    </div>
  );
}

/** Pastille compacte, pour les listes et la recherche globale. */
export function ClientChip({ company, className = "" }) {
  if (!company?.client) return null;
  const noms = clientSolutionNames(company);
  return (
    <span data-testid={`client-chip-${company.id}`}
      title={noms.length ? `Client — ${noms.join(", ")}` : "Déjà client — hors prospection"}
      className={`shrink-0 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white ${className}`}>
      CLIENT
    </span>
  );
}

export function ClientButton({ company, onSaved }) {
  const [open, setOpen] = useState(false);
  const isClient = !!company.client;
  return (
    <>
      <button data-testid="client-btn" onClick={() => setOpen(true)}
        title={isClient ? "Modifier ou retirer la mention « client »" : "Marquer comme déjà client (sort la fiche de la prospection)"}
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isClient
          ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
        <Handshake size={15} /> {isClient ? "Client" : "Déjà client"}
      </button>
      {open && (
        <ClientModal company={company} onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); onSaved?.(); }} />
      )}
    </>
  );
}

function ClientModal({ company, onClose, onSaved }) {
  const [solutions, setSolutions] = useState([]);
  const [chosen, setChosen] = useState(company.client_solution_ids || []);
  const [since, setSince] = useState(company.client_since || new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState(company.client_note || "");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${company.id}/solutions`);
      setSolutions([...(data.applicable || []), ...(data.autres || [])]);
    } catch { setSolutions([]); }
  }, [company.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) =>
    setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const save = async (client) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/companies/${company.id}/client`, {
        client, solution_ids: chosen, since: since || null, note,
      });
      toast.success(client ? data.resume || "Marquée comme cliente" : "Remise en prospection");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} testid="client-modal" label="Déjà client">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-emerald-700">
          <Handshake size={18} /> Déjà client
        </h3>
        <button onClick={onClose} aria-label="Fermer"><X size={18} /></button>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        <b className="text-slate-700">{company.nom}</b> sort des listes de prospection, du pipeline de
        qualification, des relances automatiques et des campagnes d'emailing. Les appels, emails et
        rendez-vous restent possibles depuis la fiche — c'est votre client, pas un interdit de contact.
      </p>

      <div className="mb-1 text-xs text-slate-500">Solutions achetées</div>
      {solutions.length === 0 ? (
        <p className="mb-4 text-[13px] text-slate-400">Aucune solution au catalogue — la mention « client » reste possible sans.</p>
      ) : (
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="client-solutions">
          {solutions.map((s) => {
            const on = chosen.includes(s.id);
            return (
              <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`client-solution-${s.id}`}
                aria-pressed={on}
                className={`rounded-lg border px-2.5 py-1.5 text-[13px] ${on
                  ? "border-emerald-500 bg-emerald-50 font-medium text-emerald-700"
                  : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {s.nom}
              </button>
            );
          })}
        </div>
      )}

      <label className="mb-1 block text-xs text-slate-500" htmlFor="client-since">Client depuis</label>
      <input id="client-since" data-testid="client-since" type="date" value={since || ""}
        onChange={(e) => setSince(e.target.value)}
        className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500" />

      <label className="mb-1 block text-xs text-slate-500" htmlFor="client-note">Note (facultatif)</label>
      <textarea id="client-note" data-testid="client-note" rows={2} value={note}
        onChange={(e) => setNote(e.target.value)} placeholder="Contrat, interlocuteur, échéance…"
        className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-500" />

      <div className="flex justify-end gap-2">
        {company.client && (
          <button data-testid="client-remove" disabled={saving} onClick={() => save(false)}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            Retirer — redevient un prospect
          </button>
        )}
        <button data-testid="client-save" disabled={saving} onClick={() => save(true)}
          className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50">
          {company.client ? "Enregistrer" : "Marquer comme client"}
        </button>
      </div>
    </Modal>
  );
}
